import type { Attribution } from "@ets/types";

const MAX_VALUE_LENGTH = 256;
const MAX_ALL_PARAMS = 50;

const UTM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
  "utm_id",
] as const;

const CLICK_ID_KEYS = [
  "gclid",
  "gbraid",
  "wbraid",
  "fbclid",
  "msclkid",
  "ttclid",
  "twclid",
  "liFatId",
] as const;

const KNOWN_KEYS = new Set<string>([...UTM_KEYS, ...CLICK_ID_KEYS]);

function truncate(value: string): string {
  return value.length > MAX_VALUE_LENGTH ? value.slice(0, MAX_VALUE_LENGTH) : value;
}

/**
 * Collects attribution data from URL query parameters.
 * Extracts UTM params, click IDs, and remaining params.
 * Safe to call in SSR / non-browser environments.
 */
export function collectAttribution(url?: string): Attribution {
  const result: Attribution = {};

  if (typeof window === "undefined" && url === undefined) {
    return result;
  }

  const href = url ?? (typeof window !== "undefined" ? window.location.href : undefined);

  if (!href) {
    return result;
  }

  let searchParams: URLSearchParams;
  try {
    const parsed = new URL(href);
    searchParams = parsed.searchParams;
  } catch {
    return result;
  }

  // Extract UTM params
  for (const key of UTM_KEYS) {
    const value = searchParams.get(key);
    if (value) {
      result[key] = truncate(value);
    }
  }

  // Extract click IDs
  for (const key of CLICK_ID_KEYS) {
    const value = searchParams.get(key);
    if (value) {
      result[key] = truncate(value);
    }
  }

  // Collect remaining params into `all`
  const all: Record<string, string> = {};
  let count = 0;

  for (const [key, value] of searchParams.entries()) {
    if (count >= MAX_ALL_PARAMS) break;
    if (KNOWN_KEYS.has(key)) continue;
    if (!value) continue;
    all[key] = truncate(value);
    count++;
  }

  if (Object.keys(all).length > 0) {
    result.all = all;
  }

  return result;
}
